/**
 * Presentation view: a thumbnail rail and one slide at full size (§17).
 *
 * Slides are the one document type where absolute positioning is the honest
 * rendering — the file says exactly where every shape sits, in EMU. So the
 * stage is a fixed-size box scaled to fit, and each shape is placed at its
 * declared coordinates inside it. Nothing is reflowed, because nothing in the
 * original was.
 *
 * §17 also says animations and transitions are preserved but not played. This
 * component has no animation code at all, which is a stronger guarantee than a
 * flag: there is nothing here that could start moving.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  PptxModel, PptxSlide, SlideParagraph, SlideRun, SlideShape,
} from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface SlideViewerProps {
  readonly model: PptxModel;
  readonly current: number;
  readonly editing: boolean;
  readonly search: string;
  readonly showNotes: boolean;
  readonly onSelect: (index: number) => void;
  readonly onEditShape: (slideIndex: number, shapeId: string, text: string) => void;
}

export function SlideViewer({
  model, current, editing, search, showNotes, onSelect, onEditShape,
}: SlideViewerProps): React.JSX.Element {
  const stage = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [active, setActive] = useState<string | null>(null);

  const imageUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const [part, image] of model.images) {
      urls.set(part, URL.createObjectURL(new Blob([image.bytes as BlobPart], { type: image.contentType })));
    }
    return urls;
  }, [model.images]);

  useEffect(() => () => {
    for (const url of imageUrls.values()) URL.revokeObjectURL(url);
  }, [imageUrls]);

  // Scale to fit rather than to a zoom level: a slide is a fixed canvas, and
  // seeing all of it is almost always what someone wants.
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = (): void => {
      const box = el.getBoundingClientRect();
      const fit = Math.min(box.width / model.width, box.height / model.height);
      setScale(Number.isFinite(fit) && fit > 0 ? Math.min(fit, 2) : 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [model.width, model.height]);

  useEffect(() => { setActive(null); }, [current, editing]);

  const slide = model.slides[current] ?? model.slides[0];
  if (!slide) return <div className="canvas" />;

  return (
    <div className="slide-layout">
      <nav className="slide-rail" aria-label="슬라이드 목록">
        {model.slides.map((s) => (
          <button
            key={s.part}
            className="slide-thumb"
            aria-current={s.index === current ? 'true' : undefined}
            onClick={() => onSelect(s.index)}
          >
            <span className="slide-thumb__no tnum">{s.index + 1}</span>
            <span className="slide-thumb__box" aria-hidden="true">
              <Miniature slide={s} width={model.width} height={model.height} />
            </span>
            <span className="slide-thumb__title truncate">{s.title || '제목 없음'}</span>
            {s.hidden ? (
              <Icon name="eye-off" size={12} className="slide-thumb__hidden" />
            ) : null}
            {(model.edits.get(s.part)?.size ?? 0) > 0 ? (
              <span className="slide-thumb__dot" title="변경됨" />
            ) : null}
          </button>
        ))}
      </nav>

      <div className="slide-main">
        <div className="canvas slide-stage" ref={stage}>
          <div
            className="slide-page"
            style={{
              width: model.width,
              height: model.height,
              transform: `scale(${scale})`,
            }}
            aria-label={`슬라이드 ${slide.index + 1}${slide.title ? `: ${slide.title}` : ''}`}
          >
            {slide.shapes.map((shape) => (
              <Shape
                key={shape.id}
                shape={shape}
                editing={editing}
                active={active === shape.id}
                search={search}
                imageUrls={imageUrls}
                onActivate={() => setActive(shape.id)}
                onCommit={(text) => { setActive(null); onEditShape(slide.index, shape.id, text); }}
                onCancel={() => setActive(null)}
              />
            ))}
          </div>
          {slide.hidden ? (
            <p className="slide-hidden-note">
              <Icon name="eye-off" size={13} />
              이 슬라이드는 발표에서 숨겨져 있습니다.
            </p>
          ) : null}
        </div>

        {showNotes ? (
          <aside className="slide-notes" aria-label="발표자 노트">
            {slide.notes
              ? slide.notes.split('\n').map((line, i) => <p key={i}>{line}</p>)
              : <p className="slide-notes__empty">이 슬라이드에는 발표자 노트가 없습니다.</p>}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * shapes
 * ------------------------------------------------------------------ */

interface ShapeProps {
  readonly shape: SlideShape;
  readonly editing: boolean;
  readonly active: boolean;
  readonly search: string;
  readonly imageUrls: ReadonlyMap<string, string>;
  readonly onActivate: () => void;
  readonly onCommit: (text: string) => void;
  readonly onCancel: () => void;
}

function Shape(props: ShapeProps): React.JSX.Element | null {
  const { shape } = props;
  const rect = shape.rect;
  if (!rect) return null;

  const box: React.CSSProperties = {
    position: 'absolute',
    left: rect.x,
    top: rect.y,
    width: rect.width,
    height: rect.height,
    transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
  };

  if (shape.kind === 'picture') {
    const url = shape.part ? props.imageUrls.get(shape.part) : undefined;
    if (!url) {
      return (
        <div className="slide-shape slide-shape--missing" style={box} title={shape.alt}>
          <Icon name="file-image" size={20} />
        </div>
      );
    }
    return (
      <img className="slide-shape slide-shape--pic" style={box} src={url} alt={shape.alt} />
    );
  }

  if (shape.kind === 'frame') {
    if (shape.content === 'table' && shape.rows.length > 0) {
      return (
        <div className="slide-shape" style={box}>
          <table className="slide-table">
            <tbody>
              {shape.rows.map((row, r) => (
                <tr key={r}>{row.map((cell, c) => <td key={c}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // §19: an empty frame labelled for what it is beats a blank space that
    // looks like the file was read wrong.
    return (
      <div className="slide-shape slide-shape--frame" style={box}>
        <Icon name={shape.content === 'chart' ? 'chart' : 'shapes'} size={18} />
        <span>{shape.content === 'chart' ? '차트' : shape.content === 'diagram' ? 'SmartArt' : '개체'}는 이 버전에서 표시되지 않습니다</span>
      </div>
    );
  }

  if (props.active) {
    return (
      <div className="slide-shape" style={box}>
        <ShapeEditor
          text={shape.text}
          label={`슬라이드 도형 ${shape.name || shape.id} 편집`}
          onCommit={props.onCommit}
          onCancel={props.onCancel}
        />
      </div>
    );
  }

  const canEdit = props.editing && shape.editable;
  const anchor = shape.anchor === 'middle' ? 'center' : shape.anchor === 'bottom' ? 'flex-end' : 'flex-start';

  return (
    <div
      className={`slide-shape slide-shape--text${canEdit ? ' slide-shape--editable' : ''}`}
      style={{ ...box, justifyContent: anchor }}
      {...(canEdit ? {
        role: 'button' as const,
        tabIndex: 0,
        title: '클릭하면 이 글상자를 편집합니다',
        onClick: props.onActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onActivate(); }
        },
      } : {})}
    >
      {shape.paragraphs.map((p, i) => (
        <Paragraph key={i} paragraph={p} search={props.search} />
      ))}
    </div>
  );
}

function Paragraph(
  { paragraph, search }: { paragraph: SlideParagraph; search: string },
): React.JSX.Element {
  return (
    <p
      className="slide-p"
      style={{
        textAlign: paragraph.alignment ?? undefined,
        marginInlineStart: paragraph.level ? paragraph.level * 22 : undefined,
      }}
    >
      {paragraph.level > 0 && paragraph.bulleted && paragraph.text.trim() !== '' ? (
        <span className="slide-bullet" aria-hidden="true">·</span>
      ) : null}
      {paragraph.runs.map((run, i) => <Run key={i} run={run} search={search} />)}
    </p>
  );
}

function Run({ run, search }: { run: SlideRun; search: string }): React.JSX.Element {
  if (run.text === '\n') return <br />;
  const style: React.CSSProperties = {
    fontWeight: run.bold ? 700 : undefined,
    fontStyle: run.italic ? 'italic' : undefined,
    textDecoration: run.underline ? 'underline' : undefined,
    color: run.color ? `#${run.color}` : undefined,
    fontSize: run.sizePx ? `${run.sizePx}px` : undefined,
  };
  const content = search ? highlight(run.text, search) : run.text;
  if (run.href) {
    return (
      <a className="doc-link" href={run.href} style={style} title={run.href}
        onClick={(e) => e.preventDefault()}>{content}</a>
    );
  }
  return <span style={style}>{content}</span>;
}

function ShapeEditor(
  { text: initial, label, onCommit, onCancel }: {
    text: string;
    label: string;
    onCommit: (text: string) => void;
    onCancel: () => void;
  },
): React.JSX.Element {
  const [text, setText] = useState(initial);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  return (
    <textarea
      ref={area}
      className="slide-edit"
      value={text}
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        // A slide's text box is multi-line by nature, so Enter inserts a line
        // and Ctrl+Enter commits — the opposite of the document editor, which
        // matches how each is used.
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onCommit(text); }
      }}
      onBlur={() => onCommit(text)}
    />
  );
}

/* ------------------------------------------------------------------ *
 * thumbnails
 * ------------------------------------------------------------------ */

/**
 * A thumbnail drawn from the same geometry as the slide.
 *
 * Boxes only, no text: at 96 px wide the words would be illegible anyway, and
 * the shape of a slide is what makes one recognisable in a strip. Rendering it
 * from the real coordinates rather than a generic icon means the strip
 * actually distinguishes slides.
 */
function Miniature(
  { slide, width, height }: { slide: PptxSlide; width: number; height: number },
): React.JSX.Element {
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="slide-mini" role="presentation">
      <rect x="0" y="0" width={width} height={height} className="slide-mini__bg" />
      {slide.shapes.map((shape) => {
        const r = shape.rect;
        if (!r) return null;
        return (
          <rect
            key={shape.id}
            x={r.x} y={r.y} width={r.width} height={r.height}
            className={`slide-mini__shape slide-mini__shape--${shape.kind}`}
          />
        );
      })}
    </svg>
  );
}

function highlight(text: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (;;) {
    const found = lower.indexOf(target, cursor);
    if (found < 0) break;
    if (found > cursor) out.push(text.slice(cursor, found));
    out.push(<mark key={found}>{text.slice(found, found + needle.length)}</mark>);
    cursor = found + needle.length;
  }
  if (out.length === 0) return text;
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
